/**
 * <lms-sub-header> — barre utilitaire au-dessus du header.
 *
 * Responsabilités :
 *  - Ouvrir/fermer le(s) dropdown(s) « sélecteur d'environnement » (clic, clic extérieur, Échap).
 *  - Portal mobile : sous 1024px, déplacer le slot pictos/env-selector dans le pied
 *    du menu drawer Sleek (`.menu-drawer__footer`) ; le restaurer en desktop.
 *
 * @class
 * @extends HTMLElement
 */
if (!customElements.get('lms-sub-header')) {
  customElements.define(
    'lms-sub-header',
    class LmsSubHeader extends HTMLElement {
      /**
       * Sélecteurs DOM.
       * @type {object}
       */
      #selectors = {
        mobileSlot: '[data-lms-mobile-slot]',
        disclosure: '[data-lms-disclosure]',
        disclosureToggle: '[data-lms-disclosure-toggle]',
        drawerFooter: '.menu-drawer__footer',
      };

      /**
       * Classes CSS.
       * @type {object}
       */
      #classes = {
        open: 'is-open',
      };

      /**
       * Références DOM mises en cache.
       * @type {object}
       */
      #elements = {};

      /**
       * MediaQueryList pour le basculement desktop/mobile.
       * @type {MediaQueryList|null}
       */
      #mobileQuery = null;

      /**
       * Marqueur de position d'origine du slot mobile (pour restauration).
       * @type {Comment|null}
       * @private
       */
      #slotAnchor = null;

      /**
       * Lifecycle — connecté au DOM.
       * @private
       */
      connectedCallback() {
        this.#cacheElements();
        if (!this.#isValidSetup()) return;

        this.#mobileQuery = window.matchMedia('(max-width: 1023px)');

        document.addEventListener('click', this.#handleDocumentClick);
        document.addEventListener('keydown', this.#handleKeydown);
        this.#mobileQuery.addEventListener('change', this.#handleViewportChange);

        this.#syncMobileSlot();
      }

      /**
       * Lifecycle — retiré du DOM. Nettoie listeners et restaure le slot.
       * @private
       */
      disconnectedCallback() {
        document.removeEventListener('click', this.#handleDocumentClick);
        document.removeEventListener('keydown', this.#handleKeydown);
        this.#mobileQuery?.removeEventListener('change', this.#handleViewportChange);
        this.#restoreSlot();
      }

      /**
       * Met en cache les éléments DOM.
       * @private
       */
      #cacheElements() {
        this.#elements = {
          mobileSlot: this.querySelector(this.#selectors.mobileSlot),
        };
      }

      /**
       * Valide la présence des éléments requis.
       * @private
       * @returns {boolean}
       */
      #isValidSetup() {
        if (!this.#elements.mobileSlot) {
          console.error('lms-sub-header: slot mobile introuvable');
          return false;
        }
        return true;
      }

      /**
       * Indique si un nœud appartient à cette section (barre desktop ou slot mobile déplacé).
       * @private
       * @param {Node} node
       * @returns {boolean}
       */
      #owns(node) {
        return this.contains(node) || this.#elements.mobileSlot.contains(node);
      }

      /**
       * Gère les clics document : toggle de dropdown ou fermeture au clic extérieur.
       * @private
       * @param {MouseEvent} event
       */
      #handleDocumentClick = (event) => {
        const toggle = event.target.closest(this.#selectors.disclosureToggle);

        if (toggle && this.#owns(toggle)) {
          event.preventDefault();
          this.#toggleDisclosure(toggle.closest(this.#selectors.disclosure));
          return;
        }

        this.#closeAllDisclosures(event.target);
      };

      /**
       * Ferme les dropdowns ouverts sur Échap.
       * @private
       * @param {KeyboardEvent} event
       */
      #handleKeydown = (event) => {
        if (event.key === 'Escape') this.#closeAllDisclosures(null);
      };

      /**
       * Rebascule le slot mobile lors d'un changement de viewport.
       * @private
       */
      #handleViewportChange = () => {
        this.#syncMobileSlot();
      };

      /**
       * Ouvre/ferme un dropdown (ferme les autres).
       * @private
       * @param {HTMLElement|null} disclosure
       */
      #toggleDisclosure(disclosure) {
        if (!disclosure) return;
        const willOpen = !disclosure.classList.contains(this.#classes.open);
        this.#closeAllDisclosures(null);
        if (willOpen) {
          disclosure.classList.add(this.#classes.open);
          disclosure
            .querySelector(this.#selectors.disclosureToggle)
            ?.setAttribute('aria-expanded', 'true');
        }
      }

      /**
       * Ferme tous les dropdowns appartenant à la section, sauf celui contenant `keepWithin`.
       * @private
       * @param {Node|null} keepWithin
       */
      #closeAllDisclosures(keepWithin) {
        const scopes = [this, this.#elements.mobileSlot];
        scopes.forEach((scope) => {
          scope.querySelectorAll(`${this.#selectors.disclosure}.${this.#classes.open}`).forEach((disclosure) => {
            if (keepWithin && disclosure.contains(keepWithin)) return;
            disclosure.classList.remove(this.#classes.open);
            disclosure
              .querySelector(this.#selectors.disclosureToggle)
              ?.setAttribute('aria-expanded', 'false');
          });
        });
      }

      /**
       * Place le slot mobile dans le drawer (<1024px) ou le restaure (≥1024px).
       * @private
       */
      #syncMobileSlot() {
        if (this.#mobileQuery?.matches) {
          this.#moveSlotToDrawer();
        } else {
          this.#restoreSlot();
        }
      }

      /**
       * Déplace le slot mobile dans le pied du menu drawer.
       * @private
       */
      #moveSlotToDrawer() {
        const footer = document.querySelector(this.#selectors.drawerFooter);
        const slot = this.#elements.mobileSlot;
        if (!footer || footer.contains(slot)) return;

        if (!this.#slotAnchor) {
          this.#slotAnchor = document.createComment('lms-sub-header-slot');
          slot.before(this.#slotAnchor);
        }
        slot.removeAttribute('hidden');
        footer.prepend(slot);
      }

      /**
       * Restaure le slot mobile à sa position d'origine (et le masque).
       * @private
       */
      #restoreSlot() {
        const slot = this.#elements.mobileSlot;
        if (this.#slotAnchor && this.#slotAnchor.isConnected) {
          this.#slotAnchor.replaceWith(slot);
          this.#slotAnchor = null;
        }
        slot.setAttribute('hidden', '');
      }
    }
  );
}
